import { isValidPeriod } from "@/utils/date-range";
import { ExtendedColumnDef } from "components/Datatable";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { Load } from "components/load";
import PageError from "components/page-error";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { toast } from "components/ui/use-toast";
import { format } from "date-fns";
import { IoRefresh } from "react-icons/io5";
import { MdDownload } from "react-icons/md";
import { SiMicrosoftexcel } from "react-icons/si";
import { IBalanceReport } from "src/@types/balance-report";
import { Meta } from "src/@types/paginated";
import { cn } from "../../../../lib/utils";
import { useExportTransactionByDateExcelMutation, useGetTransactionByDateQuery } from "../../../../services/store/store";
import { buttonClasses, layoutClasses } from "../../../../utils/css-classes";
import { downloadExcel } from "../../../../utils/download-excel";
import { formatFilterValue } from "../../../../utils/formatters";
import { handeOrderBy } from "../../../../utils/pagination";
import { useFilterActions } from "../ducks/filter/hooks/actions";
import { useFilterState } from "../ducks/filter/hooks/filterState";
import { columns } from "./columns";
import { BALANCE_REPORT_FILTERS_STORAGE_KEY } from "./constants";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import SelectFilters from "./components/select-filters";
import Table from "./components/table";

type StoredBalanceReportFilters = Record<string, unknown> & {
  periodStart?: string;
  periodEnd?: string;
};

const getStoredFilters = (): StoredBalanceReportFilters => {
  if (typeof localStorage === "undefined") return {};
  return JSON.parse(
    localStorage.getItem(BALANCE_REPORT_FILTERS_STORAGE_KEY) || "{}",
  );
};

const setStoredFilters = (filters: StoredBalanceReportFilters) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(
    BALANCE_REPORT_FILTERS_STORAGE_KEY,
    JSON.stringify(filters),
  );
};

export const BalanceReport: React.FC = () => {
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const {
    code,
    responsible_id,
    regional_id,
    depositStatus,
    periodStart,
    periodEnd,
    order: orderObj,
  } = filters ?? {};
  const orderBy = handeOrderBy(orderObj || { column: 'id', direction: 'desc' });

  const periodValid = isValidPeriod(periodStart, periodEnd);

  const [exportExcel, { isLoading: isExportingExcel }] = useExportTransactionByDateExcelMutation();

  const getFilters = () => ({
    ...(code !== undefined && { code: formatFilterValue(code) }),
    ...(responsible_id !== undefined && { responsible_id: formatFilterValue(responsible_id) }),
    ...(regional_id !== undefined && { regional_id: formatFilterValue(regional_id) }),
    ...(depositStatus !== undefined && depositStatus !== null && { depositStatus }),
    ...(periodStart !== undefined && {
      transactionStartDate: `${periodStart}T00:00:00.000Z`,
    }),
    ...(periodEnd !== undefined && {
      transactionEndDate: `${periodEnd}T23:59:59.999Z`,
    }),
    orderBy: JSON.stringify(orderBy),
    perPage: filters?.meta?.perPage || 10,
    page: filters?.meta?.page || 1,
  });

  const {
    data: res,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetTransactionByDateQuery(getFilters(), { skip: !periodValid });

  const handleExportExcel = async () => {
    try {
      const response = await exportExcel(getFilters()).unwrap();
      const fileName = `relatorio-saldos-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      downloadExcel(response.excelFile, fileName);
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Erro ao exportar planilha Excel. Tente novamente.",
        duration: 5000,
      });
    }
  };

  const { updateFilter } = useFilterActions();

  const handlePeriodChange = (
    start: string | undefined,
    end: string | undefined,
  ) => {
    updateFilter({ key: "periodStart", value: start });
    updateFilter({ key: "periodEnd", value: end });
    updateFilter({ key: "meta", value: { ...filters.meta, page: 1 } });

    const currentFilters = getStoredFilters();
    setStoredFilters({
      ...currentFilters,
      periodStart: start,
      periodEnd: end,
    });
  };


  if (error) {
    return <PageError status={500} message={"ERRO INTERNO NO SERVIDOR"} />;
  }

  if (isLoading) {
    return <Load />;
  }
  const { data, meta } = res ?? { data: [], meta: {} };

  return (
    <PageLayout>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 w-full">
        <PageTitle
          title={"Conciliação de Caixas"}
          description="Relatório de Saldos"
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />
        <PeriodRangeFilter
          start={periodStart}
          end={periodEnd}
          onChange={handlePeriodChange}
        />
      </div>
      <ContentContainer>
        <div className={layoutClasses.filtersContainer}>
          <SelectFilters />
          <div className={layoutClasses.actionsContainer}>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    buttonClasses.linkButton,
                    isExportingExcel ? buttonClasses.linkButtonDisabled : buttonClasses.linkButtonEnabled
                  )}
                >
                  <MdDownload className={buttonClasses.buttonIcon} />
                  {isExportingExcel ? "Exportando..." : "Exportar Relatório"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2">
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleExportExcel}
                    disabled={isExportingExcel}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-0"
                  >
                    <SiMicrosoftexcel className="w-4 h-4 text-green-600" />
                    Planilha Excel
                  </button>
                </div>
              </PopoverContent>
            </Popover>
            <button
              className={cn(
                buttonClasses.linkButton,
                isFetching ? buttonClasses.linkButtonDisabled : buttonClasses.linkButtonEnabled
              )}
              onClick={() => {
                if (!isFetching) refetch();
              }}
            >
              <IoRefresh className={buttonClasses.buttonIcon} />
              Recarregar
            </button>
          </div>
        </div>
        <div className={layoutClasses.tableContainer}>
          <div className={layoutClasses.tableWrapper}>
            <Table
              data={data as IBalanceReport[]}
              columns={columns as ExtendedColumnDef<IBalanceReport>[]}
              isFetching={isFetching}
              meta={meta as Meta}
            />
          </div>
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
